import { useState, useEffect } from 'react'
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { useBillingStatus } from "@/hooks/useBillingStatus"
import { Loader2, Check, CalendarIcon, CreditCard, AlertCircle, ChevronDown, ChevronUp } from "lucide-react"
import ProfileHeader from "./profile-header"
import ProfileSettings from "./profile-settings"
import { supabase } from '@/lib/supabase'
import { formatCurrency } from "@/lib/utils"

export interface ProfileData {
  id: string;
  full_name: string | null;
  email_opt_in: boolean | null;
  customer_status: string | null;
  account_tier: string | null;
  billing_method?: string | null;
  churn_date?: string | null;
  last_login_date?: string | null;
  onboarding_completed?: boolean | null;
  payment_status?: string | null;
  trial_days_left?: number | null;
  notificationPreferences?: {
    emailNotifications: boolean;
  } | null;
  [key: string]: any; // Allow for additional fields
}

interface BillingStatusCardProps {
  profileData: ProfileData | null;
}

interface DebugDataCardProps {
  profileData: ProfileData | null;
}

// Helper function to safely get status
const getStatus = (status: string | null | undefined): string => {
  return status?.toLowerCase() || 'unknown'
}

function BillingStatusCard({ profileData }: BillingStatusCardProps) {
  const { loading, error, billingStatus } = useBillingStatus()
  const [planDetails, setPlanDetails] = useState<any>(null)
  const [planLoading, setPlanLoading] = useState(false)

  // Add status styling helper
  const getStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'text-green-600'
      case 'trialing':
        return 'text-blue-600'
      case 'past_due':
      case 'incomplete':
        return 'text-orange-600'
      case 'canceled':
      case 'unpaid':
        return 'text-red-600'
      default:
        return 'text-muted-foreground'
    }
  }

  // Add status icon helper
  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return <Check className="h-4 w-4 text-green-600" />
      case 'trialing':
        return <CalendarIcon className="h-4 w-4 text-blue-600" />
      case 'past_due':
      case 'incomplete':
        return <AlertCircle className="h-4 w-4 text-orange-600" />
      case 'canceled':
      case 'unpaid':
        return <AlertCircle className="h-4 w-4 text-red-600" />
      default:
        return <CreditCard className="h-4 w-4 text-muted-foreground" />
    }
  }

  // Add status message helper
  const getStatusMessage = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'Your subscription is currently active'
      case 'trialing':
        return 'Your trial is currently active'
      case 'past_due':
        return 'Payment is past due - please update your payment method'
      case 'incomplete':
        return 'Payment is incomplete - please complete payment setup'
      case 'canceled':
        return 'Your subscription has been canceled'
      case 'unpaid':
        return 'Payment failed - please update your payment method'
      default:
        return null
    }
  }

  useEffect(() => {
    const fetchPlanDetails = async () => {
      if (!billingStatus?.subscription?.plan_id) return

      try {
        setPlanLoading(true)
        const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }
        
        if (!account?.account_id) {
          throw new Error('No account found')
        }

        const session = await supabase.auth.getSession()
        if (!session.data.session) {
          throw new Error('No session found')
        }

        const workerUrl = import.meta.env.VITE_WORKERS_URL
        const response = await fetch(`${workerUrl}/billing-functions/get_plan_details`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.data.session.access_token}`
          },
          body: JSON.stringify({ account_id: account.account_id })
        })

        if (!response.ok) {
          throw new Error('Failed to fetch plan details')
        }

        const plans = await response.json()
        console.log('All plans:', plans)
        const currentPlan = plans.plans.find((p: any) => p.id === billingStatus.subscription.plan_id)
        console.log('Found plan:', currentPlan, 'looking for ID:', billingStatus.subscription.plan_id)
        setPlanDetails(currentPlan)
      } catch (error) {
        console.error('Error fetching plan details:', error)
      } finally {
        setPlanLoading(false)
      }
    }

    fetchPlanDetails()
  }, [billingStatus?.subscription?.plan_id])

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading || planLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription Status</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription Status</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-500">Error loading subscription status</p>
        </CardContent>
      </Card>
    )
  }

  if (!billingStatus?.has_active_subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>You don't have an active subscription.</p>
          <Link to="/settings">
            <Button>View Plans</Button>
          </Link>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Plan Details Section */}
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-lg">
                {planDetails?.name || 'Unknown Plan'}
              </h3>
              {planDetails?.description && (
                <p className="text-sm text-muted-foreground">{planDetails.description}</p>
              )}
            </div>
            <div className="text-right">
              <p className="font-semibold">
                {planDetails && formatCurrency(planDetails.price, planDetails.currency)}
              </p>
              <p className="text-sm text-muted-foreground">
                per {planDetails?.interval || 'month'}
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <Separator />

        {/* Enhanced Billing Status Section */}
        <div className="space-y-4">
          {/* Customer Status */}
          <div className="flex items-center gap-2 text-sm">
            {getStatusIcon(getStatus(profileData?.customer_status))}
            <span className="text-muted-foreground">Customer Status:</span>
            <span className={`capitalize font-medium ${getStatusStyle(getStatus(profileData?.customer_status))}`}>
              {profileData?.customer_status || 'Unknown'}
            </span>
          </div>

          {/* Status Message */}
          {getStatusMessage(getStatus(profileData?.customer_status)) && 
           getStatus(profileData?.customer_status) !== 'active' && (
            <div className={`flex items-start gap-2 rounded-md p-3 text-sm ${
              getStatus(profileData?.customer_status) === 'trialing' 
                ? 'bg-blue-50' 
                : getStatus(profileData?.customer_status) === 'past_due' || getStatus(profileData?.customer_status) === 'incomplete'
                  ? 'bg-orange-50'
                  : 'bg-red-50'
            }`}>
              <AlertCircle className={`h-5 w-5 mt-0.5 ${
                getStatus(profileData?.customer_status) === 'trialing'
                  ? 'text-blue-600'
                  : getStatus(profileData?.customer_status) === 'past_due' || getStatus(profileData?.customer_status) === 'incomplete'
                    ? 'text-orange-600'
                    : 'text-red-600'
              }`} />
              <div>
                <p className={`font-medium ${
                  getStatus(profileData?.customer_status) === 'trialing'
                    ? 'text-blue-800'
                    : getStatus(profileData?.customer_status) === 'past_due' || getStatus(profileData?.customer_status) === 'incomplete'
                      ? 'text-orange-800'
                      : 'text-red-800'
                }`}>
                  {getStatus(profileData?.customer_status) === 'trialing' ? 'Trial Active' : 'Action Required'}
                </p>
                <p className={
                  getStatus(profileData?.customer_status) === 'trialing'
                    ? 'text-blue-700'
                    : getStatus(profileData?.customer_status) === 'past_due' || getStatus(profileData?.customer_status) === 'incomplete'
                      ? 'text-orange-700'
                      : 'text-red-700'
                }>
                  {getStatusMessage(getStatus(profileData?.customer_status))}
                </p>
              </div>
            </div>
          )}

          {/* Billing Period */}
          <div className="flex items-center gap-2 text-sm">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Current Period:</span>
            <span>
              {formatDate(billingStatus.subscription.billing_period_start)} - {formatDate(billingStatus.subscription.billing_period_end)}
            </span>
          </div>

          {/* Existing Cancellation Warning */}
          {billingStatus.subscription.cancel_at_period_end && (
            <div className="flex items-start gap-2 rounded-md bg-yellow-50 p-3 text-sm">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Subscription will end</p>
                <p className="text-yellow-700">
                  Your subscription will cancel on {formatDate(billingStatus.subscription.billing_period_end)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Features Section */}
        {planDetails?.features && planDetails.features.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <h4 className="font-medium">Included Features</h4>
              <ul className="space-y-2">
                {planDetails.features.map((feature: string, i: number) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {/* Action Button */}
        <div className="pt-4">
          <Link to="/settings">
            <Button className="w-full">
              Manage Subscription
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

function DebugDataCard({ profileData }: DebugDataCardProps) {
  const { loading, error, billingStatus } = useBillingStatus()
  const [isExpanded, setIsExpanded] = useState(false)
  const [authData, setAuthData] = useState<any>(null)

  useEffect(() => {
    const fetchAuthData = async () => {
      if (!isExpanded) return

      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return

        setAuthData({
          session: {
            ...session,
            // Remove sensitive data
            access_token: '[REDACTED]',
            refresh_token: '[REDACTED]',
          },
          user: session.user,
          metadata: {
            user_metadata: session.user.user_metadata,
            app_metadata: session.user.app_metadata,
          }
        })
      } catch (error) {
        console.error('Error fetching auth data:', error)
      }
    }

    fetchAuthData()
  }, [isExpanded])

  if (loading) {
    return null
  }

  if (error) {
    return null
  }

  return (
    <Card className="mt-6">
      <CardHeader className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-muted-foreground">Debug Information</CardTitle>
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">Auth Data</h4>
              <p className="text-sm text-muted-foreground mb-2">
                From <code>auth.users</code> table and current session. Contains authentication information including 
                user identity, metadata, and session details. This is the core user record managed by Supabase Auth.
              </p>
              <pre className="bg-slate-50 p-4 rounded-md overflow-auto text-xs">
                {JSON.stringify(authData, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-medium mb-2">Marketing Profile Data</h4>
              <p className="text-sm text-muted-foreground mb-2">
                From <code>marketing_and_customer_success.profiles</code> table. Contains customer profile information including account tier, 
                onboarding status, and marketing preferences. This data is used for customer segmentation and lifecycle management.
              </p>
              <pre className="bg-slate-50 p-4 rounded-md overflow-auto text-xs">
                {JSON.stringify(profileData, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-medium mb-2">Customer Data</h4>
              <p className="text-sm text-muted-foreground mb-2">
                From <code>basejump.billing_customers</code> table. Contains core customer billing information including Stripe customer ID,
                billing status, and payment periods. This is the primary record of a customer's billing relationship.
              </p>
              <pre className="bg-slate-50 p-4 rounded-md overflow-auto text-xs">
                {JSON.stringify(billingStatus.customer, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-medium mb-2">Subscription Data</h4>
              <p className="text-sm text-muted-foreground mb-2">
                From <code>basejump.billing_subscriptions</code> table. Contains detailed subscription information including plan details,
                trial periods, and renewal dates. This tracks the specific subscription plan and its current state.
              </p>
              <pre className="bg-slate-50 p-4 rounded-md overflow-auto text-xs">
                {JSON.stringify(billingStatus.subscription, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-medium mb-2">Full Billing Status</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Combined billing status from the <code>useBillingStatus</code> hook. Aggregates customer, subscription, and active status
                information. This is used throughout the application to determine the customer's billing state.
              </p>
              <pre className="bg-slate-50 p-4 rounded-md overflow-auto text-xs">
                {JSON.stringify(billingStatus, null, 2)}
              </pre>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  )
}

export default function Profile() {
  const [profileData, setProfileData] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }
        
        if (!account?.account_id) {
          console.error('No account found')
          return
        }

        const { data, error } = await supabase
          .schema('marketing_and_customer_success')
          .from('profiles')
          .select('*')
          .eq('id', account.account_id)
          .single()

        if (error) {
          console.error('Error fetching profile:', error)
          return
        }

        // Parse notificationPreferences if it's a JSON string
        const parsedData = {
          ...data,
          notificationPreferences: typeof data.notificationPreferences === 'string' 
            ? JSON.parse(data.notificationPreferences)
            : data.notificationPreferences
        }

        setProfileData(parsedData)
      } catch (error) {
        console.error('Error in fetchProfileData:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchProfileData()
  }, [])

  return (
    <div className="container mx-auto p-6 space-y-6">
      <ProfileHeader profileData={profileData} />
      <Separator className="my-6" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProfileSettings profileData={profileData} loading={loading} />
        <div className="space-y-6">
          <BillingStatusCard profileData={profileData} />
          {process.env.NODE_ENV === 'development' && <DebugDataCard profileData={profileData} />}
        </div>
      </div>
    </div>
  )
} 
